import React, { useMemo, useState } from 'react';
import { IconButton, MenuItem, Typography, Menu } from '@mui/material';
import { PatternFormat } from 'react-number-format';
import Table from '../common/Table';
import { hasIdentityVerification } from '../common/DeleteVerificationModal';
import { MoreHoriz } from '@mui/icons-material';

const GuestsTable = ({
    members,
    isRental,
    onMakeGuestOccupant,
    onMakeGuestTenant,
    onGuestSuspend,
    onGuestDelete,
    onDeleteVerification,
    globalFilter,
    canManage,
    propertyId
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);

    const handleMenuClick = (event, member) => {
        setAnchorEl(event.currentTarget);
        setSelectedMember(member);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedMember(null);
    };

    const handleMakeOccupant = () => {
        onMakeGuestOccupant?.(selectedMember);
        handleMenuClose();
    };

    const handleMakeTenant = () => {
        onMakeGuestTenant?.(selectedMember);
        handleMenuClose();
    };

    const handleSuspendGuest = () => {
        onGuestSuspend(selectedMember);
        handleMenuClose();
    };

    const handleDeleteGuest = () => {
        onGuestDelete(selectedMember);
        handleMenuClose();
    };

    const handleDeleteVerification = () => {
        onDeleteVerification?.(selectedMember);
        handleMenuClose();
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: row => `${row.first_name || ''} ${row.last_name || ''}`.trim(),
                Cell: ({ row }) => `${row.original.first_name} ${row.original.last_name}`
            },
            {
                Header: 'Phone',
                accessor: row =>
                    `${row.phone.code || ''}${row.phone.number || ''}`.trim(),
                Cell: ({ row }) => (
                    <Typography
                        sx={{ fontSize: '13px' }}
                        component="span"
                        variant="body1"
                        color="text.primary"
                    >
                        <PatternFormat
                            value={row.original?.phone?.number || ''}
                            displayType={'text'}
                            format={`+${row.original?.phone?.code} (###)-###-####`}
                        />
                    </Typography>
                )
            },
            {
                Header: 'Status',
                accessor: 'status',
                className: 'cell-center',
                Cell: ({ value }) => (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '80%',
                            width: '30%',
                            backgroundColor:
                                value === 'Active'
                                    ? 'rgba(45, 143, 87, .1)'
                                    : 'rgba(255, 0, 0, .1)',
                            borderRadius: '5px'
                        }}
                    >
                        <Typography
                            sx={{ fontSize: '13px', marginX: '10px' }}
                            component="span"
                            variant="body1"
                            color={
                                value === 'Active'
                                    ? 'rgba(45, 143, 87, 1)'
                                    : 'rgba(255, 0, 0, 1)'
                            }
                        >
                            {value}
                        </Typography>
                    </div>
                )
            },
            ...(canManage || onDeleteVerification
                ? [
                      {
                          Header: 'Actions',
                          className: 'cell-right',
                          Cell: ({ row }) => (
                              <IconButton
                                  onClick={e => {
                                      e.stopPropagation();
                                      handleMenuClick(e, row.original);
                                  }}
                              >
                                  <MoreHoriz />
                              </IconButton>
                          )
                      }
                  ]
                : [])
        ],
        [canManage, onDeleteVerification]
    );

    return (
        <>
            <Table columns={columns} data={members} externalGlobalFilter={globalFilter} />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {canManage && onMakeGuestOccupant && (
                    <MenuItem onClick={handleMakeOccupant}>Make Occupant</MenuItem>
                )}

                {canManage && onMakeGuestTenant && isRental && (
                    <MenuItem onClick={handleMakeTenant}>Make Tenant</MenuItem>
                )}

                {canManage && (
                    <MenuItem to="/" onClick={handleSuspendGuest}>
                        {selectedMember?.properties &&
                        selectedMember?.properties[propertyId]?.suspended
                            ? 'Unsuspend'
                            : 'Suspend'}
                    </MenuItem>
                )}

                {canManage && <MenuItem onClick={handleDeleteGuest}>Delete</MenuItem>}

                {onDeleteVerification && (
                    <MenuItem
                        onClick={handleDeleteVerification}
                        disabled={!hasIdentityVerification(selectedMember)}
                    >
                        Delete Verification
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default GuestsTable;
